import { z } from 'zod';

/** The code policy's bounds for `auth.verificationCode.length` (P30 decision 12). */
export const MIN_VERIFICATION_CODE_LENGTH = 4;
export const MAX_VERIFICATION_CODE_LENGTH = 8;

/**
 * The live challenge on one contact — every fact a screen draws around the
 * code field, stamped by the **server's** clock: the resend button counts
 * down to `resendAvailableAt`, the code field draws `codeLength` cells, the
 * hint reads `attemptsRemaining`. A client never derives a deadline (P13c2a).
 */
export const OpenChallengeSchema = z.object({
  resendAvailableAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  attemptsRemaining: z.number().int().min(0),
  codeLength: z.number().int().min(MIN_VERIFICATION_CODE_LENGTH).max(MAX_VERIFICATION_CODE_LENGTH),
});

export type OpenChallenge = z.infer<typeof OpenChallengeSchema>;

/**
 * One contact as the member sees it: what it is (`target`, `null` until they
 * give it), whether it is proven, and the open code on it if there is one —
 * `challenge: null` means nothing to type: verified, expired, exhausted, or
 * never sent, and the screen offers a send.
 */
export const ChannelVerificationStateSchema = z.object({
  target: z.string().nullable(),
  verified: z.boolean(),
  challenge: OpenChallengeSchema.nullable(),
});

export type ChannelVerificationState = z.infer<typeof ChannelVerificationStateSchema>;

/**
 * `GET /auth/verification/state` — owned by `auth`, which holds the challenge
 * table; `/me` carries only what `users` can answer from its own columns and
 * the settings (`onboarding`). Both channels are always present so a client
 * branches on facts, never on the shape (P30 decision 13).
 */
export const VerificationStateSchema = z.object({
  email: ChannelVerificationStateSchema,
  phone: ChannelVerificationStateSchema,
});

export type VerificationState = z.infer<typeof VerificationStateSchema>;
